/**
 * Shifts Hooks
 *
 * Provides hooks for shift management with TanStack Query.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

// ─── Types ──────────────────────────────────────────────────────────────

export interface ShiftSlot {
  id: string;
  position: number;
  startTime: string;
  endTime: string;
  daysOfWeek: number[];
  color: string;
}

export interface ShiftGroupMember {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface ShiftGroup {
  id: string;
  name: string;
  position: number;
  members: ShiftGroupMember[];
}

export interface ShiftPattern {
  id: string;
  name: string;
  description: string | null;
  rotationType: 'fixed' | 'weekly_rotation';
  rotationPeriodWeeks: number;
  referenceDate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  slots: ShiftSlot[];
  groups: ShiftGroup[];
}

export interface ShiftPatternListItem {
  id: string;
  name: string;
  description: string | null;
  rotationType: string;
  rotationPeriodWeeks: number;
  referenceDate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ComputedShift {
  userId: string;
  date: string;
  startTime: string;
  endTime: string;
  color: string;
  groupId: string;
  groupName: string;
  slotId: string;
  isOverridden: boolean;
  overrideType?: string;
}

export interface CreateShiftSlotData {
  position: number;
  startTime: string;
  endTime: string;
  daysOfWeek?: number[];
  color?: string;
}

export interface CreateShiftGroupData {
  name: string;
  position: number;
  memberUserIds?: string[];
}

export interface CreateShiftPatternData {
  name: string;
  description?: string | null;
  rotationType: 'fixed' | 'weekly_rotation';
  rotationPeriodWeeks?: number;
  referenceDate: string;
  slots: CreateShiftSlotData[];
  groups: CreateShiftGroupData[];
}

export interface UpdateShiftPatternData {
  name?: string;
  description?: string | null;
  rotationType?: 'fixed' | 'weekly_rotation';
  rotationPeriodWeeks?: number;
  referenceDate?: string;
  isActive?: boolean;
  slots?: CreateShiftSlotData[];
  groups?: CreateShiftGroupData[];
}

export interface CreateOverrideData {
  userId: string;
  date: string;
  overrideType: 'custom_time' | 'day_off' | 'swap';
  startTime?: string;
  endTime?: string;
  swapWithUserId?: string;
  reason?: string | null;
}

// ─── Pattern Hooks ──────────────────────────────────────────────────────

export function useShiftPatterns() {
  return useQuery({
    queryKey: queryKeys.shifts.patterns.lists(),
    queryFn: () => api.get<{ data: ShiftPatternListItem[] }>('/shifts/patterns'),
    select: (res) => res.data,
    staleTime: 60_000,
  });
}

export function useShiftPattern(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.shifts.patterns.detail(id ?? ''),
    queryFn: () => api.get<ShiftPattern>(`/shifts/patterns/${id}`),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCreateShiftPattern() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateShiftPatternData) =>
      api.post<ShiftPattern>('/shifts/patterns', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all });
    },
  });
}

export function useUpdateShiftPattern() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateShiftPatternData }) =>
      api.put<ShiftPattern>(`/shifts/patterns/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all });
    },
  });
}

export function useDeactivateShiftPattern() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/shifts/patterns/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all });
    },
  });
}

// ─── Schedule Hooks ─────────────────────────────────────────────────────

export function useShiftSchedule(params: {
  startDate: string;
  endDate: string;
  userId?: string;
  groupId?: string;
}) {
  return useQuery({
    queryKey: queryKeys.shifts.schedule(params),
    queryFn: () => {
      const searchParams = new URLSearchParams();
      searchParams.set('startDate', params.startDate);
      searchParams.set('endDate', params.endDate);
      if (params.userId) searchParams.set('userId', params.userId);
      if (params.groupId) searchParams.set('groupId', params.groupId);
      return api.get<{ data: ComputedShift[] }>(`/shifts/schedule?${searchParams.toString()}`);
    },
    select: (res) => res.data,
    staleTime: 30_000,
    enabled: !!params.startDate && !!params.endDate,
    placeholderData: keepPreviousData,
  });
}

// ─── Override Hooks ─────────────────────────────────────────────────────

export function useCreateShiftOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateOverrideData) =>
      api.post('/shifts/overrides', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all });
    },
  });
}

export function useDeleteShiftOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/shifts/overrides/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all });
    },
  });
}

// ─── Group Member Hooks ─────────────────────────────────────────────────

export function useAddGroupMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      api.post(`/shifts/groups/${groupId}/members`, { userId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all });
    },
  });
}

export function useRemoveGroupMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      api.delete(`/shifts/groups/${groupId}/members/${userId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.shifts.all });
    },
  });
}
